import React, { useEffect, useRef, useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useReferralCampaigns } from '../api/useReferralCampaigns';
import { CampaignStatus, MAX_PAGE_SIZE, PromoCampaign } from '../api/types';
import { formatCount, formatDateTime } from '../format';
import {
  CampaignStatusBadge,
  EmptyRow,
  FallbackDealBadge,
  TableWrap,
  tdClass,
  tdNumClass,
  thClass,
  thNumClass,
} from './controls';

const selectClass =
  'px-2.5 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-gray-200 text-xs focus:outline-none focus:border-violet-500';

function EligibleBusinesses({ campaign }: { campaign: PromoCampaign }) {
  const businesses = campaign.eligible_businesses ?? [];
  if (businesses.length === 0) {
    return <span className="text-xs text-gray-500">any business</span>;
  }
  const shown = businesses.slice(0, 2).map((b) => b.name ?? b.business_id);
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-300 truncate">{shown.join(', ')}</p>
      {businesses.length > shown.length && (
        <p className="text-xs text-gray-500">+{businesses.length - shown.length} more</p>
      )}
    </div>
  );
}

function CapCell({ campaign }: { campaign: PromoCampaign }) {
  if (campaign.max_claims == null) {
    return <span className="text-xs text-gray-500">uncapped · {formatCount(campaign.claim_count)} claimed</span>;
  }
  const pct = Math.min(100, Math.round((campaign.claim_count / campaign.max_claims) * 100));
  const full = campaign.claims_remaining === 0;
  return (
    <div className="min-w-[120px]">
      <p className={`text-xs ${full ? 'text-amber-300' : 'text-gray-300'}`}>
        {formatCount(campaign.claim_count)} / {formatCount(campaign.max_claims)}
        {full ? ' · full' : ''}
      </p>
      <div className="mt-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div className={`h-full ${full ? 'bg-amber-500' : 'bg-violet-500'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Deal-campaign list. The `personal_default` row is pinned to the top and
 * labelled as the fallback deal — it is a promo_campaigns row like any other,
 * but every un-attached code grants it, so it reads differently.
 */
export function CampaignList({
  env,
  onCreate,
  onEdit,
  refreshKey = 0,
}: {
  env: Env;
  onCreate: () => void;
  onEdit: (campaign: PromoCampaign) => void;
  refreshKey?: number;
}) {
  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [q, setQ] = useState('');

  const { data, loading, error, refetch } = useReferralCampaigns(env, {
    status: status || undefined,
    q: q.trim() || undefined,
    sort: 'created_at',
    order: 'desc',
    limit: MAX_PAGE_SIZE,
  });

  const lastRefresh = useRef(refreshKey);
  useEffect(() => {
    if (lastRefresh.current !== refreshKey) {
      lastRefresh.current = refreshKey;
      refetch();
    }
  }, [refreshKey, refetch]);

  // Fallback first, then the backend's newest-first order.
  const campaigns = [...(data?.campaigns ?? [])].sort((a, b) => {
    if (a.kind === b.kind) return 0;
    return a.kind === 'personal_default' ? -1 : 1;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name or deal text"
            className="px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-gray-200 text-xs w-52 focus:outline-none focus:border-violet-500"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as CampaignStatus | '')}
            className={selectClass}
            aria-label="Campaign status"
          >
            <option value="">Any status</option>
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="ended">ended</option>
          </select>
        </div>
        <button
          type="button"
          onClick={onCreate}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500"
        >
          + New campaign
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <TableWrap>
          <thead className="bg-gray-900/60">
            <tr>
              <th className={thClass}>Campaign</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>Window</th>
              <th className={thClass}>Claims</th>
              <th className={thNumClass}>Granted</th>
              <th className={thNumClass}>Redeemed</th>
              <th className={thClass}>Eligible businesses</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {campaigns.length === 0 && <EmptyRow span={8}>No campaigns match these filters.</EmptyRow>}
            {campaigns.map((c) => (
              <tr key={c.id} className="hover:bg-gray-900/40">
                <td className={tdClass}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white">{c.name}</span>
                      {c.kind === 'personal_default' && <FallbackDealBadge />}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 max-w-md">{c.deal_description}</p>
                  </div>
                </td>
                <td className={tdClass}>
                  <CampaignStatusBadge status={c.status} />
                </td>
                <td className={tdClass}>
                  <p className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(c.starts_at)}</p>
                  <p className="text-xs text-gray-500 whitespace-nowrap">to {formatDateTime(c.ends_at)}</p>
                </td>
                <td className={tdClass}>
                  <CapCell campaign={c} />
                </td>
                <td className={tdNumClass}>{formatCount(c.grants?.granted ?? 0)}</td>
                <td className={tdNumClass}>{formatCount(c.grants?.redeemed ?? 0)}</td>
                <td className={tdClass}>
                  <EligibleBusinesses campaign={c} />
                </td>
                <td className={tdClass}>
                  <button
                    type="button"
                    onClick={() => onEdit(c)}
                    className="px-2 py-1 text-xs font-medium rounded-md bg-gray-800 text-gray-200 hover:bg-gray-700"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
